import { useCallback } from "react";
import { toast } from "@/components/Toaster";
import { getApiErrorMessage } from "@/lib/error-toast";
import { useAuth } from "@/hooks/useAuth";
import { allocationRemainderCents } from "@capture/shared";
import { useStationStore } from "../state/station-store";
import { useLiveDerivation } from "../hooks/useLiveDerivation";
import { canBalanceRemittance } from "../lib/permissions";
import { canAcceptTransaction } from "../lib/accept-gate";
import { explainPolicyOutcome } from "../lib/policy-explain";
import { acceptTransaction, overrideTransaction, getStation, applyCheckAmount, putAllocations } from "../api/client";
import { RepairControls } from "./RepairControls";
import { AllocationTable } from "./AllocationTable";
import type { StationTransaction } from "../api/client";
import { formatMoneyCents as money } from "../lib/money";
import styles from "./RemittanceStation.module.css";

export function TransactionPanel({ transaction }: { transaction: StationTransaction }) {
  const { user } = useAuth();
  const canBalance = canBalanceRemittance(user);
  const payload = useStationStore((s) => s.payload);
  // Accept permission is MODE-CONDITIONAL (matches the Task 9 server route):
  // split "key" step accept requires remittance.key EXACTLY — the server key
  // route has NO balance⇒key fallback, so we check membership directly here
  // (canKeyRemittance's fallback would wrongly enable a balance-only operator
  // whose click then 403s). Combined/balance modes need remittance.balance.
  // Override + repair stay balance-gated (always balance-affecting) — see
  // canBalance below.
  const canAcceptPerm = payload?.batch.mode === "key"
    ? !!user?.permissions?.includes("remittance.key")
    : canBalanceRemittance(user);
  const removeTransaction = useStationStore((s) => s.removeTransaction);
  const loadPayload = useStationStore((s) => s.loadPayload);
  const allocationDraft = useStationStore((s) => s.allocationDraft[transaction.id]);
  // F-013 — derive through the hook (subscribes to the state the derivation
  // reads) so keying-pane pushes (setDocAmount) re-render the Σ/Δ readout,
  // verdict chip, and Accept gate live. The store's livePolicyOutcome getter
  // is stable and would NOT re-render on those pushes.
  const live = useLiveDerivation(transaction.id);
  const snap = live?.snapshot ?? null;
  const allocationConfigured = !!payload?.config.allocation;
  // SP7 seam (a) client mirror: judge the remainder against the live-derived
  // final set (policy rows included) — the same set the server gate now uses.
  const remainder = allocationConfigured
    ? allocationRemainderCents(snap?.paymentTotalCents ?? 0, live?.finalAllocations ?? transaction.allocations ?? [])
    : 0;
  // SP7 §7 — hard rule flags block acceptance client-side, mirroring the
  // server's parity check (transaction-actions.ts step 1b): a hard-flagged
  // transaction 409s on accept unless it's been overridden.
  const hardBlocked = !!snap?.ruleFlags?.some((f) => f.severity === "hard") && transaction.overriddenAt === null;
  const explain = live
    ? explainPolicyOutcome({ live, targets: payload?.config.allocation, overridden: transaction.overriddenAt !== null })
    : null;
  const acceptable = !hardBlocked && !!snap && canAcceptTransaction({
    verdict: snap.verdict, overridden: transaction.overriddenAt !== null, allocationConfigured, allocationRemainderCents: remainder,
  }) && canAcceptPerm;

  const refetch = useCallback(async () => {
    if (payload) loadPayload(await getStation(payload.batch.id));
  }, [payload, loadPayload]);

  const onAccept = useCallback(async () => {
    if (!acceptable) return;
    // Flush the in-memory allocation draft BEFORE accept so the server reads
    // exactly the rows the client gate evaluated its remainder from. The draft
    // may have auto-seeded (autofillAllocations → remainder 0) without ever
    // being persisted (persist only happens on an input onBlur), so without
    // this the server would read an empty transaction.allocations column and
    // 409 "remainder must be zero" on the balanced happy path.
    if (allocationConfigured) {
      try {
        await putAllocations(transaction.id, allocationDraft ?? transaction.allocations ?? []);
      } catch (e) {
        toast.error(getApiErrorMessage(e, { fallback: "Couldn't save the allocation — try again." }));
        return; // abort accept if the allocation didn't persist
      }
    }
    try {
      const res = await acceptTransaction(transaction.id);
      if (res.status === "accepted") { removeTransaction(transaction.id); toast.success(`Transaction ${transaction.ordinal} accepted`); }
    } catch (e) {
      // Surface the server's actual rejection reason — the real text lives in
      // ApiError.body (409 `{status, message}` reconcile failures, 403 `{error}`
      // permission), NOT ApiError.message (generic "API error 409").
      // getApiErrorMessage reads body.message then body.error, covering both.
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't accept — check the balance and try again." }));
    }
  }, [acceptable, allocationConfigured, allocationDraft, transaction.id, transaction.ordinal, transaction.allocations, removeTransaction]);

  // SP7 fix — a hard rule can fire on a BALANCED transaction; without an
  // override affordance the disabled Accept is an operator dead end. The
  // server override route doesn't gate on verdict, and overriddenAt bypasses
  // hard rules on both the server accept path and explainPolicyOutcome.
  const hardBlockOnly = hardBlocked && snap?.verdict !== "imbalanced";

  const onOverride = useCallback(async () => {
    if (!payload) return;
    const reason = window.prompt(
      hardBlockOnly
        ? `Override the rule block on Transaction ${transaction.ordinal}? Enter a reason:`
        : `Override the imbalance on Transaction ${transaction.ordinal}? Enter a reason:`,
    );
    if (!reason?.trim()) return;
    await overrideTransaction(payload.batch.id, transaction.id, reason.trim());
    await refetch();
  }, [payload, hardBlockOnly, transaction.id, transaction.ordinal, refetch]);

  // G2a — one-keystroke "apply check amount": server (Task 9) writes the
  // coupon amounts from the payment total + audits; refetch pulls the
  // rewritten fields and the rebalanced snapshot.
  const onApplyCheckAmount = useCallback(async () => {
    try {
      await applyCheckAmount(transaction.id);
      await refetch();
      toast.success("Applied the check amount to the coupon(s)");
    } catch (e) {
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't apply check amount — try again." }));
    }
  }, [transaction.id, refetch]);

  const siblings = (payload?.transactions ?? []).filter((t) => t.id !== transaction.id).map((t) => ({ id: t.id, label: `Transaction ${t.ordinal}`, ordinal: t.ordinal }));

  return (
    <aside className={styles.right} data-testid="station-right">
      <div className={styles.balanceBlock}>
        <div>Σ payment <b>{money(snap?.paymentTotalCents ?? 0)}</b></div>
        <div>Σ applied <b>{money(snap?.appliedTotalCents ?? 0)}</b></div>
        <div className={styles.delta} data-tone={snap?.verdict === "balanced" || transaction.overriddenAt ? "ok" : snap?.verdict === "indeterminate" ? "warn" : "error"}>
          Δ {money(snap?.deltaCents ?? 0)} — {transaction.overriddenAt ? "overridden" : snap?.verdict ?? "—"}
        </div>
        {transaction.duplicateSuspect && (
          <div className={styles.dupChip} role="status" title="Possible duplicate deposit" aria-label="Possible duplicate deposit">
            ⚠ Possible duplicate deposit
          </div>
        )}
        {/* SP7 §7 — LIVE policy state: chips render from the live derivation
            (same core as the server's accept decision); the durable
            policyAdjustment badge survives balanced recomputes (seam c). */}
        {(snap?.adjustment ?? transaction.policyAdjustment) && (
          <div className={styles.adjustedBadge} role="status">Balanced · policy-adjusted</div>
        )}
        {snap?.policyFlag && (
          <div className={styles.policyFlagChip} role="status">
            {snap.policyFlag === "shortPay" ? "In-policy short-pay" : "In-policy overpay"}
          </div>
        )}
        {!!snap?.ruleFlags?.length && (
          <ul className={styles.ruleFlags}>
            {snap.ruleFlags.map((f) => (
              <li key={f.ruleId} className={styles.ruleFlag} data-severity={f.severity}>{f.message}</li>
            ))}
          </ul>
        )}
        {explain && <div className={styles.policyExplain} role="status">{explain}</div>}
      </div>

      {allocationConfigured && payload?.config.allocation && (
        <AllocationTable
          transaction={transaction}
          config={payload.config.allocation}
          paymentTotalCents={snap?.paymentTotalCents ?? 0}
          canEdit={canBalance}
        />
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.acceptBtn} disabled={!acceptable} onClick={onAccept} title="Ctrl+Enter">
          Accept transaction (Ctrl+Enter)
        </button>
        {canBalance && (snap?.verdict === "imbalanced" || hardBlocked) && !transaction.overriddenAt && (
          <button type="button" className={styles.overrideBtn} onClick={onOverride}>
            {hardBlockOnly ? "Override rule block…" : "Override imbalance…"}
          </button>
        )}
        {canBalance && transaction.documents.some((d) => d.role === "coupon") && transaction.documents.some((d) => d.role === "payment") && (
          <button type="button" className={styles.applyCheckBtn} onClick={onApplyCheckAmount}>
            Apply check amount
          </button>
        )}
      </div>

      {canBalance && <RepairControls transaction={transaction} siblings={siblings} onRepaired={refetch} />}
    </aside>
  );
}
